// src/oura/components/fixes/FixRows.tsx
//
// The rows of the grouped Fixes list: an open fix with its status select, a
// done fix as one line, a proposal waiting on a decision, and a
// recommendation the plan has no step for.
//
// Every row carries a hairline above it except the first of its group, and
// every task row keeps the id `tracker-task-<id>` the week plan uses, so a
// `?task=` link lands in either view.

import { useState, type ReactNode } from "react";
import { ChevronRightIcon, LightbulbIcon } from "lucide-react";
import type { Recommendation } from "../../audit";
import { blockLabel, type TaskStatus, type TrackerSuggestion, type TrackerTask } from "../../tracker";
import { cn } from "../../lib/cn";
import { ArrowLink, Dot, FOCUS_RING, LATE_TEXT, PillButton, STAFF_STATUS, TYPE, type StaffStatus, ROW_HOVER } from "@oura/ui";
import { StatusSelect } from "./TaskParts";
import { OWNER_LABEL, daysLate, doneLabel, dueLabel, lateLabel, movesOf, type PlacedTask } from "./model";

/**
 * The row frame. `continued` is for rows inside "Show N more": the first of
 * them is not the first of its group, so it keeps its hairline.
 */
function rowFrame(continued?: boolean): string {
    return cn("flex w-full gap-3 border-t border-gray-100 py-3", !continued && "first:border-t-0");
}

/** A deep-linked row is tinted on arrival and fades back: emphasis, not a selection. */
function arrival(highlighted: boolean): string {
    return cn("scroll-mt-24 transition-colors duration-700 focus-visible:outline-none", highlighted && "bg-blue-50 duration-150");
}

/** The meta line: parts joined by a light middle dot, a late note last and in rose. */
function MetaLine({ parts, late, muted }: { parts: ReactNode[]; late?: string; muted?: boolean }) {
    const items = late ? parts.concat(<span className={LATE_TEXT}>{late}</span>) : parts;
    return (
        <p className={cn(TYPE.tm, "m-0 mt-1 flex flex-wrap items-center gap-x-1.5", muted ? "text-gray-400" : "text-gray-500")}>
            {items.map((part, i) => (
                <span key={i} className="inline-flex items-center gap-x-1.5">
                    {i > 0 && <span aria-hidden className="text-gray-300">·</span>}
                    {part}
                </span>
            ))}
        </p>
    );
}

/** One open fix: dot, title (opens the drawer), why, meta, and its status. */
export function FixRow({
    task,
    group,
    why,
    today,
    highlighted,
    onOpen,
    onSetStatus,
}: {
    task: PlacedTask;
    group: StaffStatus;
    why: string | null;
    today: string;
    highlighted: boolean;
    onOpen: () => void;
    onSetStatus: (t: TrackerTask, s: TaskStatus) => void;
}) {
    const muted = STAFF_STATUS[group].muted === true;
    const late = daysLate(task, today);
    const moves = movesOf(task);
    const due = dueLabel(task);
    const parts: ReactNode[] = [OWNER_LABEL[task.owner]];
    if (moves.length) parts.push(`Moves ${moves.join(", ")}`);
    parts.push(blockLabel(task.block));
    if (due && !late) parts.push(due);
    return (
        <div id={`tracker-task-${task.id}`} tabIndex={-1} className={cn(rowFrame(), "flex-wrap items-start", arrival(highlighted))}>
            <Dot tone={STAFF_STATUS[group].tone} className="mt-1.5" />
            <div className="min-w-0 flex-1 basis-48">
                <button
                    type="button"
                    onClick={onOpen}
                    className={cn(
                        TYPE.tb,
                        "rounded text-left font-medium hover:text-blue-600",
                        muted ? "text-gray-400" : "text-gray-900",
                        task.resolved && "line-through",
                        FOCUS_RING,
                    )}
                >
                    {task.action}
                </button>
                {why && <p className={cn(TYPE.tc, "m-0", muted ? "text-gray-400" : "text-gray-500")}>{why}</p>}
                <MetaLine parts={parts} late={late ? lateLabel(late) : undefined} muted={muted} />
            </div>
            {/* On a phone the select wraps under the text, lined up with it. */}
            <StatusSelect value={task.status} onChange={(s) => onSetStatus(task, s)} label={`Status of ${task.action}`} className="ml-5 sm:ml-0" />
        </div>
    );
}

/** A done fix, as one line that opens the drawer. */
export function FixLine({
    task,
    highlighted,
    continued,
    onOpen,
}: {
    task: PlacedTask;
    highlighted: boolean;
    continued?: boolean;
    onOpen: () => void;
}) {
    return (
        <button
            id={`tracker-task-${task.id}`}
            type="button"
            onClick={onOpen}
            className={cn(rowFrame(continued), "min-h-12 items-center py-2 text-left", ROW_HOVER, arrival(highlighted), FOCUS_RING)}
        >
            <Dot tone="green" />
            <span className={cn(TYPE.tb, "min-w-0 flex-1 truncate text-gray-900 group-hover:text-blue-600")}>{task.action}</span>
            <span className={cn(TYPE.tc, "shrink-0 whitespace-nowrap text-gray-500")}>{doneLabel(task)}</span>
            <ChevronRightIcon aria-hidden size={14} className="shrink-0 text-gray-400" />
        </button>
    );
}

/**
 * Work the rules propose, waiting on a person (ourteam-backend#4513).
 *
 * Adding it to the plan keeps it (decision `keep`): it becomes a task through
 * the generator, so a rebuild reproduces it. Dismissing stops it being
 * proposed again for this business. `why` is shown in full because it is the
 * same sentence that reaches the client.
 */
export function ProposedRow({
    suggestion,
    onDecide,
}: {
    suggestion: TrackerSuggestion;
    onDecide: (id: string, decision: "keep" | "dismiss") => Promise<boolean>;
}) {
    const [busy, setBusy] = useState<"keep" | "dismiss" | null>(null);
    const sg = suggestion;
    const decide = async (decision: "keep" | "dismiss") => {
        setBusy(decision);
        await onDecide(sg.id, decision);
        setBusy(null);
    };
    const urls = sg.evidence?.urls ?? [];
    return (
        <div className={cn(rowFrame(), "flex-wrap items-start")}>
            <LightbulbIcon aria-hidden size={16} className="mt-0.5 shrink-0 text-blue-600" />
            <div className="min-w-0 flex-1 basis-48">
                <p className={cn(TYPE.tb, "m-0 font-medium text-gray-900")}>{sg.action}</p>
                {/* The evidence first: it decides whether the work is worth keeping. */}
                <p className={cn(TYPE.tc, "m-0 text-gray-600")}>{sg.why}</p>
                {sg.notes && <p className={cn(TYPE.tm, "m-0 mt-1 text-gray-500")}>{sg.notes}</p>}
                {urls.length > 0 && (
                    <ul className="list-plain m-0 mt-1 space-y-0.5 p-0">
                        {urls.slice(0, 4).map((u, i) => (
                            <li key={u} className={cn(TYPE.tm, "truncate text-gray-500")}>
                                {sg.evidence?.titles?.[i] || u}
                            </li>
                        ))}
                        {urls.length > 4 && <li className={cn(TYPE.tm, "text-gray-500")}>And {urls.length - 4} more</li>}
                    </ul>
                )}
                <MetaLine parts={[`Proposed for ${blockLabel(sg.week)}`]} />
            </div>
            <div className="ml-7 flex shrink-0 items-center gap-2 sm:ml-0">
                {/* Dismiss quiet, Add to plan the considered act: the queue is
                    only worth reading if adding is a decision, not the default. */}
                <PillButton
                    variant="text"
                    size="sm"
                    busy={busy === "dismiss"}
                    disabled={busy !== null}
                    aria-label={`Dismiss ${sg.action}`}
                    writes
                    onClick={() => void decide("dismiss")}
                >
                    {busy === "dismiss" ? "Dismissing" : "Dismiss"}
                </PillButton>
                <PillButton
                    size="sm"
                    busy={busy === "keep"}
                    disabled={busy !== null}
                    aria-label={`Add ${sg.action} to the plan`}
                    writes
                    onClick={() => void decide("keep")}
                >
                    {busy === "keep" ? "Adding" : "Add to plan"}
                </PillButton>
            </div>
        </div>
    );
}

const PRIORITY_LABEL: Record<Recommendation["priority"], string> = {
    high: "High priority",
    medium: "Medium priority",
    low: "Low priority",
};

/**
 * A recommendation the plan has no step for.
 *
 * Not offered as a task: a hand-made task is deleted by the next rebuild, so
 * the way in is Refresh plan or a proposal. Where it names a live check it
 * opens that finding, which is where drafting happens.
 *
 * The meta line states the priority alone: under the "Not in the plan" head
 * the group already says it, and where the plan could not be read
 * (`planKnown` false) whether the plan covers it is not known. That row is
 * not greyed either, since nothing says the work is out of the plan.
 */
export function PlanGapRow({
    rec,
    planKnown = true,
    onOpenCheck,
}: {
    rec: Recommendation;
    planKnown?: boolean;
    onOpenCheck?: () => void;
}) {
    const muted = planKnown;
    return (
        <div className={cn(rowFrame(), "flex-wrap items-start", onOpenCheck && ROW_HOVER)}>
            <Dot tone="gray" className="mt-1.5" />
            <div className="min-w-0 flex-1 basis-48">
                <p className={cn(TYPE.tb, "m-0 font-medium", muted ? "text-gray-400" : "text-gray-900")}>{rec.title}</p>
                <p className={cn(TYPE.tc, "m-0", muted ? "text-gray-400" : "text-gray-500")}>{rec.impact}</p>
                <MetaLine parts={[PRIORITY_LABEL[rec.priority]]} muted={muted} />
            </div>
            {onOpenCheck && <ArrowLink stretch onClick={onOpenCheck} className="ml-5 sm:ml-0">Open in Site issues</ArrowLink>}
        </div>
    );
}
